<template>
	<v-container
		px-0
		grid-list-md
	>
		<div class="title">
			<slot name="title">Upload Image</slot>
		</div>
		<div style="position: relative;">
			<div>
				<input
					ref="uploader"
					type="file"
					accept="Image/*"
					class="image-input-upload"
					multiple="multiple"
					@change="inputImage"
				/>
				<v-form ref="urlForm" v-model="urlFormValid" @submit.prevent="upload(externalURL)">
					<v-layout row align-center my-2>
						<v-flex xs12>
							<v-text-field
								v-model="externalURL"
								:rules="[...validate.require, validateURL(externalURL)]"
								label="Image URL or Paste Image"
								@paste="paste"
							/>
						</v-flex>
						<v-flex>
							<v-btn
								:disabled="!urlFormValid || item.changing"
								color="primary"
								@click="upload(externalURL.trim())"
							>
								Add
							</v-btn>
						</v-flex>
					</v-layout>
				</v-form>
				<v-layout
					class="image-dropper grey lighten-2"
					:class="{ secondary: imageUploadActive }"
					row
					mb-3
					mx-0
					align-center
					justify-center
					@dragover.prevent="imageUploadActive = true"
					@dragenter.prevent=""
					@dragleave.prevent="imageUploadActive = false"
					@drop.prevent="dropImage"
					@click.prevent="clickImage"
					@paste="paste"
				>
					<div class="text-xs-center">
						<div class="headline">Drop Image Here</div>
						<div class="body">click to open dialog</div>
					</div>
				</v-layout>
				<v-layout
					v-if="item.images"
					row
					wrap
					mx-0
					:class="{ 'images-container': item.images.length > 9 }"
				>
					<v-flex v-for="image in item.images" :key="image" xs4>
						<v-card
							flat
							tile
							:class="{ 'image-selected': image === item.image }"
							class="d-flex image"
							@click="selectImage(image)"
						>
							<v-img
								:src="image"
								:aspect-ratio="16/9"
								class="grey lighten-2"
							>
								<template slot="placeholder">
									<v-layout
										fill-height
										align-center
										justify-center
										ma-0
									>
										<v-progress-circular indeterminate color="grey lighten-5" />
									</v-layout>
								</template>
							</v-img>
						</v-card>
					</v-flex>
				</v-layout>
			</div>
			<div v-if="item.changing" class="images-overlay-loading">
				<v-progress-circular indeterminate />
			</div>
		</div>
		<v-divider class="my-3" />
		<div class="text-xs-center">
			<v-btn
				color="primary"
				:disabled="item.changing"
				@click="selectImage('')"
			>
				Set No Image
			</v-btn>
			<v-btn
				v-if="closable"
				color="error"
				:disabled="item.changing"
				@click="$emit('close')"
			>
				Close
			</v-btn>
		</div>
	</v-container>
</template>

<script>
import Validation from '@/mixins/Validation';

export default {
	props: {
		item: { type: Object, required: true },
		loadable: { type: Boolean, default: true },
		closable: { type: Boolean, default: false },
	},
	mixins: [Validation],
	data() {
		return {
			externalURL: '',
			imageUploadActive: false,
			urlFormValid: false,
			fileTypes: ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'tif'],
		};
	},
	methods: {
		upload(data) {
			this.item.uploadImage(data).then(r => {

				if (r.data.fetchedErrors && r.data.fetchedErrors[0]) {
					this.$notify('error', r.data.fetchedErrors[0]);
					return;
				}

				this.$notify('success', 'Image uploaded.');
				this.$refs.urlForm.reset();
				this.$emit('upload');
			}).catch(({ response }) => {
				if (this.$refs.urlForm) {
					this.$refs.urlForm.reset();
				}
				if (response && response.response.status === 403) {
					this.$notify('error', 'Insufficient permissions to change image.');
					return;
				}
				this.$notify('error', 'Failed to upload image.');
			});
		},
		uploadFile(file) {
			if (!this.validFileExtension(file)) {
				this.$notify('error', 'Invalid file format.');
				return;
			}
			const reader = new FileReader();
			reader.onload = (event) => {
				this.upload(event.target.result);
			};
			reader.readAsDataURL(file);
		},
		selectImage(image) {
			if (this.item.image === image || this.item.changing) {
				return;
			}

			this.item.image = image;
			this.item.save().then(() => {
				this.$notify('success', 'Image updated.');
			}).catch(() => {
				this.$notify('error', 'Failed to update image.');
			});
		},
		dropImage(e) {
			const files = [].slice.call(e.dataTransfer.files);
			files.forEach((file) => {
				this.uploadFile(file);
			});
			this.imageUploadActive = false;
		},
		clickImage() {
			if (this.item.changing) {
				return;
			}

			this.$refs.uploader.click();
		},
		inputImage() {
			const files = Object.values(this.$refs.uploader.files);
			files.forEach((file) => {
				this.uploadFile(file);
			});
		},
		paste(e) {
			if (e.clipboardData.files.length === 0) {
				return;
			}
			const files = Object.values(e.clipboardData.files || {});
			files.forEach((file) => {
				const reader = new FileReader();
				reader.onload = () => {
					this.upload(reader.result, file.name);
				};
				reader.readAsDataURL(file);
			});
		},
		validFileExtension(file) {
			const extension = file.name.split('.').pop().toLowerCase();
			if (this.fileTypes.indexOf(extension) > -1) {
				return true;
			}
			return false;
		},
	},
};
</script>

<style lang="scss">
.image {
	:not(.v-image__image--preload) + .v-image__image {
		z-index: 0;
	}
}
</style>

<style lang="scss" scoped>
.images-overlay-loading {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: rgba(255, 255, 255, 0.8);
}
.images-container {
	overflow-y: scroll;
	max-height: 450px;
}
.image {
	border: thick solid transparent;
	cursor: pointer;
	&:hover {
		border-color: #20CAE7;
	}
	&-dropper {
		height: 100px;
		cursor: pointer;
	}
	&-selected {
		border-color: #1d97c1;
	}
	&-input-upload {
		position: absolute;
		visibility: hidden;
		width: 0;
		height: 0;
	}
}
</style>
